/**
 * antd-shaped components, rendered with the new design system.
 *
 * WHY THIS EXISTS
 * ---------------
 * `antd` is gone from `package.json`. Its remaining ~215 call sites across 45
 * files used a small, well-defined slice of its API, and this module reproduces
 * exactly that slice on top of shadcn (`base-luma`) / Base UI so the dependency
 * could be removed in one step rather than a screen at a time.
 *
 * This is a BRIDGE. Everything here renders the new design already, so there is
 * no visual debt — but the antd-shaped props are not the destination. When you
 * touch a screen, migrate its imports to the real components
 * (`#app/components/ui/*`, `#app/components/new-ui/*`) and delete what stops
 * being used. Nothing new should import from here.
 *
 * Deliberate behaviour differences are commented at each component.
 */
import {
  IconChevronDown,
  IconEye,
  IconEyeOff,
  IconX,
} from "@tabler/icons-react";
import {
  type ComponentProps,
  type ReactElement,
  type ReactNode,
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ColorField,
  DateField,
  FileChip,
  FileDropzone as FileDropzoneImpl,
  NumberField,
  TimeField,
} from "#app/components/new-ui/form-controls";
import { PaginationBlock } from "#app/components/new-ui/pagination-block";
import { PillTabs } from "#app/components/new-ui/pill-tabs";
import { Timeline as TimelineImpl } from "#app/components/new-ui/timeline";
import {
  Alert as UIAlert,
  AlertDescription,
  AlertTitle,
} from "#app/components/ui/alert";
import {
  Avatar as UIAvatar,
  AvatarFallback,
  AvatarImage,
} from "#app/components/ui/avatar";
import { Button } from "#app/components/ui/button";
import {
  Card as UICard,
  CardContent,
  CardHeader,
  CardTitle,
} from "#app/components/ui/card";
import { Checkbox as UICheckbox } from "#app/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "#app/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu";
import { Input as UIInput } from "#app/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "#app/components/ui/input-group";
import { Label } from "#app/components/ui/label";
import {
  RadioGroup as UIRadioGroup,
  RadioGroupItem,
} from "#app/components/ui/radio-group";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "#app/components/ui/select";
import { Separator } from "#app/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "#app/components/ui/sheet";
import { Skeleton as UISkeleton } from "#app/components/ui/skeleton";
import { Spinner } from "#app/components/ui/spinner";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "#app/components/ui/table";
import {
  Tabs as UITabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "#app/components/ui/tabs";
import { Textarea } from "#app/components/ui/textarea";
import { SimpleTooltip } from "#app/components/ui/tooltip";
import { cn } from "#app/utils/misc.tsx";

/* -------------------------------------------------------------------------- */
/* Re-exports — these already have a real new-design home.                     */
/* -------------------------------------------------------------------------- */

export { Modal } from "#app/components/new-ui/modal";
export { Steps } from "#app/components/new-ui/steps";
export type { Step as StepProps } from "#app/components/new-ui/steps";
/** antd's `mode` has no effect — the trail is always left-aligned. */
export function Timeline(
  props: ComponentProps<typeof TimelineImpl> & { mode?: string },
) {
  const { mode: _mode, ...rest } = props;
  return <TimelineImpl {...rest} />;
}

export type { TimelineItem } from "#app/components/new-ui/timeline";
export { ConfirmPopover as Popconfirm } from "#app/components/new-ui/confirm-popover";

/**
 * antd's `<InputNumber>`.
 *
 * NOT a bare re-export of `NumberField`, which is what this was. Base UI's
 * NumberField reports through `onValueChange`, and `NumberField` destructures a
 * closed set of props — so the `onChange` that `<Form.Item>` injects was
 * silently dropped on the floor. Nothing ever reached the form store, the
 * control stayed pinned to `value={store.get(name)}` (i.e. undefined), and the
 * number you typed vanished the moment the field lost focus and Base UI
 * reconciled its edit buffer back to the controlled prop.
 *
 * Every other control here already does this translation (`Select`,
 * `Checkbox`, `Radio.Group`, `ColorPicker`); InputNumber was the one that
 * skipped it.
 */
export function InputNumber({
  value,
  onChange,
  ...props
}: Omit<ComponentProps<typeof NumberField>, "value" | "onValueChange"> & {
  /** `Form.Item` hands back whatever is in the store, which is not typed. */
  value?: unknown;
  /** antd fires with the bare value, as Base UI does — no synthetic event. */
  onChange?: (value: number | null) => void;
}) {
  // Defensive coercion: a workflow's `default` is free-form JSON, so the store
  // can hold a numeric string for a `number` field. Base UI expects a number.
  const numeric = (() => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  })();

  return (
    <NumberField
      {...props}
      value={numeric}
      onValueChange={(next) => onChange?.(next)}
    />
  );
}

/** antd's `<ColorPicker showText format="hex">`; `format` is always hex here. */
export function ColorPicker({
  showText: _showText,
  format: _format,
  ...props
}: ComponentProps<typeof ColorField> & {
  showText?: boolean;
  format?: string;
}) {
  return <ColorField {...props} />;
}

/** antd's `<DatePicker showTime picker>`. */
export function DatePicker({
  showTime,
  picker,
  style,
  ...props
}: Omit<ComponentProps<typeof DateField>, "withTime" | "granularity"> & {
  showTime?: boolean;
  picker?: string;
  style?: React.CSSProperties;
}) {
  return (
    <DateField
      withTime={showTime}
      granularity={
        picker === "week" ? "week" : picker === "month" ? "month" : "day"
      }
      style={style}
      {...props}
    />
  );
}

export function TimePicker({
  style,
  ...props
}: ComponentProps<typeof TimeField> & { style?: React.CSSProperties }) {
  return <TimeField style={style} {...props} />;
}
export { message } from "#app/utils/message";
export { StatusTag as Tag } from "#app/components/new-ui/status-tag";

/* -------------------------------------------------------------------------- */
/* Input                                                                       */
/* -------------------------------------------------------------------------- */

type AntSize = "small" | "middle" | "large";

const sizeClass = (size?: AntSize) =>
  size === "small" ? "h-8 text-xs" : size === "large" ? "h-11" : undefined;

type InputProps = Omit<
  ComponentProps<typeof UIInput>,
  "size" | "prefix" | "ref"
> & {
  /** antd exposes `InputRef`; the DOM node already has focus()/blur()/select(). */
  ref?: React.Ref<any>;
  size?: AntSize;
  prefix?: ReactNode;
  suffix?: ReactNode;
  allowClear?: boolean;
  /** antd's borderless/filled switch. Only `borderless` changes anything here. */
  variant?: "outlined" | "borderless" | "filled" | "underlined";
  status?: "error" | "warning";
  /** antd's Enter hook. */
  onPressEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Fires when `allowClear`'s X is pressed. */
  onClear?: () => void;
};

// Base UI fixes controlled vs uncontrolled at mount, so a Form.Item control
// must be controlled from its first render or a later value is ignored.
function controlledFromMount<P extends { value?: unknown }>(props: P): P {
  return "value" in props && props.value == null
    ? { ...props, value: "" }
    : props;
}

function InputBase({
  size,
  prefix,
  suffix,
  allowClear,
  variant,
  status,
  onPressEnter,
  onKeyDown,
  onClear,
  className,
  ...rest
}: InputProps) {
  const props = controlledFromMount(rest);
  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.key === "Enter") onPressEnter?.(event);
  };
  const clearable = allowClear && props.value != null && props.value !== "";

  if (!prefix && !suffix && !clearable) {
    return (
      <UIInput
        className={cn(
          sizeClass(size),
          variant === "borderless" && "border-transparent shadow-none",
          status === "error" && "border-destructive",
          className,
        )}
        aria-invalid={status === "error" || undefined}
        onKeyDown={keyDown}
        {...props}
      />
    );
  }

  return (
    <InputGroup
      className={cn(
        sizeClass(size),
        // `variant` used to be honoured only on the no-prefix branch above, so an
        // input with a `prefix` silently kept the InputGroup's filled background
        // and focus ring no matter what was asked for. The group's border is
        // already transparent; the fill and the ring are what have to go.
        variant === "borderless" &&
          "bg-transparent shadow-none has-[[data-slot=input-group-control]:focus-visible]:border-transparent has-[[data-slot=input-group-control]:focus-visible]:ring-0",
        className,
      )}
    >
      {prefix ? (
        <InputGroupAddon align="inline-start">{prefix}</InputGroupAddon>
      ) : null}
      <InputGroupInput
        aria-invalid={status === "error" || undefined}
        onKeyDown={keyDown as never}
        {...(props as ComponentProps<typeof InputGroupInput>)}
      />
      {suffix || clearable ? (
        <InputGroupAddon align="inline-end">
          {clearable ? (
            <InputGroupButton
              size="icon-xs"
              variant="ghost"
              aria-label="Clear"
              onClick={() => {
                onClear?.();
                props.onChange?.({
                  target: { value: "" },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
            >
              <IconX size={12} />
            </InputGroupButton>
          ) : null}
          {suffix}
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  );
}

function InputTextArea({
  autoSize,
  rows,
  className,
  showCount: _showCount,
  status,
  onPressEnter,
  onKeyDown,
  ...props
}: ComponentProps<typeof Textarea> & {
  /** antd grows the box as you type; `field-sizing-content` is the CSS answer. */
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  /** antd only, not reproduced — no caller renders the counter. */
  showCount?: boolean;
  status?: "error" | "warning";
  onPressEnter?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}) {
  return (
    <Textarea
      rows={rows}
      aria-invalid={status === "error" || undefined}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.key === "Enter") onPressEnter?.(event);
      }}
      className={cn(autoSize && "field-sizing-content", className)}
      {...props}
    />
  );
}

function InputPassword({ className, ...rest }: InputProps) {
  const props = controlledFromMount(rest);
  const [visible, setVisible] = useState(false);
  return (
    <InputGroup className={className}>
      <InputGroupInput
        type={visible ? "text" : "password"}
        {...(props as ComponentProps<typeof InputGroupInput>)}
      />
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          size="icon-xs"
          variant="ghost"
          aria-label={visible ? "Hide password" : "Show password"}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <IconEyeOff size={14} /> : <IconEye size={14} />}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}

export const Input = Object.assign(InputBase, {
  TextArea: InputTextArea,
  Password: InputPassword,
  Search: InputBase,
});

/* -------------------------------------------------------------------------- */
/* Select                                                                      */
/* -------------------------------------------------------------------------- */

export type SelectProps = {
  value?: string | number | null | string[] | readonly string[];
  defaultValue?: string | number | null | string[] | readonly string[];
  onChange?: (value: any, option?: any) => void;
  options?: Array<{
    label?: ReactNode;
    value?: string | number;
    disabled?: boolean;
    [key: string]: unknown;
  }>;
  placeholder?: ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  size?: AntSize;
  children?: ReactNode;
  /**
   * antd only. Search, tags and multi-select are NOT reproduced — no current
   * call site uses them for anything a plain select does not cover. If you need
   * them, use `#app/components/ui/combobox` rather than extending this.
   */
  showSearch?: boolean;
  searchValue?: string;
  autoClearSearchValue?: boolean;
  optionFilterProp?: string;
  optionLabelProp?: string;
  mode?: string;
  dropdownRender?: (node: ReactNode) => ReactNode;
  onSearch?: (value: string) => void;
  onSelect?: (value: any, option: any) => void;
  filterSort?: (a: any, b: any) => number;
  labelInValue?: boolean;
  maxTagCount?: number | "responsive";
  suffixIcon?: ReactNode;
  ref?: React.Ref<any>;
  allowClear?: boolean;
  variant?: string;
  getPopupContainer?: unknown;
  filterOption?: boolean | ((input: string, option: any) => boolean);
  popupMatchSelectWidth?: boolean | number;
  popupClassName?: string;
  /** antd only — the option node is whatever `options[].label` renders. */
  optionRender?: (option: any) => ReactNode;
  tagRender?: (props: any) => ReactNode;
  listHeight?: number;
  dropdownStyle?: React.CSSProperties;
  notFoundContent?: ReactNode;
  style?: React.CSSProperties;
  status?: "error" | "warning";
  id?: string;
  /** Focuses the trigger on mount, as antd does. */
  autoFocus?: boolean;
};

function SelectBase({
  value,
  defaultValue,
  onChange,
  options,
  placeholder,
  className,
  disabled,
  loading,
  size,
  children,
  id,
  style,
  autoFocus,
  optionRender,
  popupMatchSelectWidth,
}: SelectProps) {
  const items = useMemo(() => {
    if (!options) return undefined;
    const map: Record<string, ReactNode> = {};
    for (const option of options) {
      if (option.value == null) continue;
      const label = option.label;
      map[String(option.value)] =
        typeof label === "string" || typeof label === "number"
          ? String(label)
          : label == null
            ? String(option.value)
            : segmentedLabelText(label) || String(option.value);
    }
    return map;
  }, [options]);

  return (
    <UISelect
      // Multi-select is not reproduced (see the note on `mode`); an array value
      // falls back to its first entry rather than rendering nothing.
      value={
        value == null
          ? undefined
          : String(Array.isArray(value) ? (value[0] ?? "") : value)
      }
      defaultValue={
        defaultValue == null
          ? undefined
          : String(
              Array.isArray(defaultValue)
                ? (defaultValue[0] ?? "")
                : defaultValue,
            )
      }
      onValueChange={(next) => next != null && onChange?.(String(next))}
      // antd fires with (value, option); no compat caller reads the second arg.
      disabled={disabled || loading}
      items={items}
    >
      <SelectTrigger
        id={id}
        style={style}
        autoFocus={autoFocus}
        className={cn(sizeClass(size), className)}
      >
        {loading ? <Spinner className="size-3.5" /> : null}
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        className={
          popupMatchSelectWidth === false
            ? "w-auto min-w-(--anchor-width)"
            : undefined
        }
        alignItemWithTrigger={popupMatchSelectWidth !== false}
      >
        {options
          ? options.map((option) => (
              <SelectItem
                key={String(option.value)}
                value={String(option.value)}
                disabled={option.disabled}
              >
                {optionRender
                  ? optionRender({
                      ...option,
                      value: option.value,
                      label: option.label,
                      data: option,
                    })
                  : (option.label as ReactNode)}
              </SelectItem>
            ))
          : children}
      </SelectContent>
    </UISelect>
  );
}

function SelectOption({
  value,
  children,
  disabled,
}: {
  value: string | number;
  children?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <SelectItem value={String(value)} disabled={disabled}>
      {children}
    </SelectItem>
  );
}

export const Select = Object.assign(SelectBase, { Option: SelectOption });

/* -------------------------------------------------------------------------- */
/* Skeleton                                                                    */
/* -------------------------------------------------------------------------- */

function SkeletonBase({
  paragraph,
  title,
  className,
}: {
  paragraph?: boolean | { rows?: number };
  title?: boolean;
  className?: string;
  active?: boolean;
  loading?: boolean;
  avatar?: boolean;
}) {
  const rows =
    typeof paragraph === "object"
      ? (paragraph.rows ?? 3)
      : paragraph === false
        ? 0
        : 3;
  return (
    <div className={cn("space-y-2", className)}>
      {title !== false ? <UISkeleton className="h-5 w-2/5" /> : null}
      {Array.from({ length: rows }).map((_, index) => (
        <UISkeleton
          key={index}
          className={cn("h-4", index === rows - 1 && "w-3/5")}
        />
      ))}
    </div>
  );
}

const skeletonSize = (size?: AntSize | "default") =>
  size === "small" ? "h-6" : size === "large" ? "h-10" : "h-8";

function SkeletonInput({
  size,
  className,
}: {
  size?: AntSize;
  active?: boolean;
  className?: string;
}) {
  return <UISkeleton className={cn(skeletonSize(size), "w-full", className)} />;
}

function SkeletonButton({
  size,
  shape,
  className,
}: {
  size?: AntSize;
  active?: boolean;
  shape?: "circle" | "round" | "square" | "default";
  className?: string;
}) {
  return (
    <UISkeleton
      className={cn(
        skeletonSize(size),
        shape === "circle" ? "aspect-square rounded-full" : "w-24 rounded-full",
        className,
      )}
    />
  );
}

export const Skeleton = Object.assign(SkeletonBase, {
  Input: SkeletonInput,
  Button: SkeletonButton,
  Avatar: SkeletonButton,
});

/* -------------------------------------------------------------------------- */
/* Radio / Checkbox                                                            */
/* -------------------------------------------------------------------------- */

const RadioGroupContext = createContext<{ disabled?: boolean } | null>(null);

function RadioGroupBase({
  value,
  onChange,
  className,
  disabled,
  children,
}: {
  value?: string | number | null;
  onChange?: (event: { target: { value: any } }) => void;
  className?: string;
  disabled?: boolean;
  children?: ReactNode;
}) {
  const context = useMemo(() => ({ disabled }), [disabled]);
  return (
    <RadioGroupContext.Provider value={context}>
      <UIRadioGroup
        value={value == null ? undefined : String(value)}
        // antd hands the caller a synthetic change event, not the bare value.
        onValueChange={(next) =>
          next != null && onChange?.({ target: { value: String(next) } })
        }
        disabled={disabled}
        className={className}
      >
        {children}
      </UIRadioGroup>
    </RadioGroupContext.Provider>
  );
}

function RadioBase({
  value,
  children,
  className,
  disabled,
}: {
  value: string | number;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  const group = useContext(RadioGroupContext);
  const id = `radio-${String(value)}`;
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <RadioGroupItem
        id={id}
        value={String(value)}
        disabled={disabled ?? group?.disabled}
      />
      {children ? <Label htmlFor={id}>{children}</Label> : null}
    </div>
  );
}

export const Radio = Object.assign(RadioBase, {
  Group: RadioGroupBase,
  Button: RadioBase,
});

export function Checkbox({
  checked,
  onChange,
  disabled,
  className,
  children,
  id,
}: {
  checked?: boolean;
  // antd passes an event; the shadcn Checkbox passes the boolean.
  onChange?: (event: { target: { checked: boolean } }) => void;
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
  id?: string;
}) {
  const generated = useRef(`cb-${Math.random().toString(36).slice(2)}`);
  const fieldId = id ?? generated.current;
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <UICheckbox
        id={fieldId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) =>
          onChange?.({ target: { checked: next === true } })
        }
      />
      {children ? <Label htmlFor={fieldId}>{children}</Label> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Typography                                                                  */
/* -------------------------------------------------------------------------- */

const TITLE_CLASS: Record<number, string> = {
  1: "text-3xl font-semibold",
  2: "text-2xl font-semibold",
  3: "text-xl font-semibold",
  4: "text-lg font-medium",
  5: "text-base font-medium",
};

export const Typography = {
  Text: ({
    type,
    className,
    strong,
    ...rest
  }: ComponentProps<"span"> & {
    type?: "secondary" | "success" | "warning" | "danger";
    strong?: boolean;
  }) => (
    <span
      className={cn(
        strong && "font-semibold",
        type === "secondary" && "text-muted-foreground",
        type === "success" && "text-emerald-600 dark:text-emerald-300",
        type === "warning" && "text-amber-600 dark:text-amber-300",
        type === "danger" && "text-destructive",
        className,
      )}
      {...rest}
    />
  ),
  Title: ({
    level = 1,
    className,
    ...props
  }: ComponentProps<"h1"> & { level?: 1 | 2 | 3 | 4 | 5 }) => {
    const Tag = `h${level}` as "h1";
    return (
      <Tag
        className={cn("font-heading", TITLE_CLASS[level], className)}
        {...props}
      />
    );
  },
  Paragraph: ({ className, ...props }: ComponentProps<"p">) => (
    <p className={cn("leading-relaxed", className)} {...props} />
  ),
  Link: ({ className, ...props }: ComponentProps<"a">) => (
    <a
      className={cn(
        "text-primary underline-offset-4 hover:underline",
        className,
      )}
      {...props}
    />
  ),
};

/* -------------------------------------------------------------------------- */
/* Layout odds and ends                                                        */
/* -------------------------------------------------------------------------- */

export function Divider({
  className,
  ...props
}: ComponentProps<typeof Separator>) {
  return <Separator className={cn("my-4", className)} {...props} />;
}

export function Alert({
  message,
  description,
  type = "info",
  showIcon: _showIcon,
  onClose,
  className,
  action,
  children,
  closable,
}: {
  message?: ReactNode;
  description?: ReactNode;
  type?: "info" | "success" | "warning" | "error";
  showIcon?: boolean;
  onClose?: () => void;
  className?: string;
  action?: ReactNode;
  closable?: boolean;
  children?: ReactNode;
  style?: React.CSSProperties;
  banner?: boolean;
}) {
  return (
    <UIAlert
      variant={type === "error" ? "destructive" : "default"}
      className={cn(
        type === "warning" &&
          "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-300",
        type === "success" &&
          "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-300",
        className,
      )}
    >
      {message ? <AlertTitle>{message}</AlertTitle> : null}
      {description ? <AlertDescription>{description}</AlertDescription> : null}
      {children}
      {action}
      {onClose || closable ? (
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="Close"
          className="absolute top-2 right-2"
          onClick={onClose}
        >
          <IconX size={14} />
        </Button>
      ) : null}
    </UIAlert>
  );
}

function CardMeta({
  title,
  description,
  avatar,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  avatar?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-start gap-3", className)}>
      {avatar}
      <div className="min-w-0">
        {title ? <p className="font-medium">{title}</p> : null}
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </div>
  );
}

function CardBase({
  title,
  children,
  className,
  size: _size,
  variant: _variant,
  type: _type,
  extra,
  ...props
}: Omit<ComponentProps<"div">, "title"> & {
  title?: ReactNode;
  size?: AntSize;
  variant?: string;
  type?: string;
  extra?: ReactNode;
}) {
  return (
    <UICard className={className} {...props}>
      {title || extra ? (
        <CardHeader className="flex-row items-center justify-between">
          {title ? <CardTitle>{title}</CardTitle> : null}
          {extra}
        </CardHeader>
      ) : null}
      <CardContent>{children}</CardContent>
    </UICard>
  );
}

export const Card = Object.assign(CardBase, { Meta: CardMeta });

export function Avatar({
  src,
  size,
  style,
  className,
  children,
}: {
  src?: string | null;
  size?: number | AntSize;
  style?: React.CSSProperties;
  className?: string;
  children?: ReactNode;
}) {
  const px = typeof size === "number" ? size : undefined;
  return (
    <UIAvatar
      className={cn(
        !px && size === "small" && "size-6",
        !px && size === "large" && "size-10",
        className,
      )}
      style={{ ...(px ? { width: px, height: px } : {}), ...style }}
    >
      {src ? <AvatarImage src={src} /> : null}
      <AvatarFallback>{children}</AvatarFallback>
    </UIAvatar>
  );
}

/* -------------------------------------------------------------------------- */
/* List                                                                        */
/* -------------------------------------------------------------------------- */

function ListItem({
  className,
  children,
  title: _title,
  description: _description,
  ...props
}: Omit<ComponentProps<"li">, "title"> & {
  size?: AntSize;
  title?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <li
      className={cn(
        "flex items-center gap-3 px-4 py-3 not-last:border-b",
        className,
      )}
      {...props}
    >
      {children}
    </li>
  );
}

function ListBase<T>({
  dataSource = [],
  renderItem,
  bordered,
  className,
  size: _size,
  children,
}: {
  dataSource?: readonly T[];
  renderItem?: (item: T, index: number) => ReactNode;
  bordered?: boolean;
  className?: string;
  size?: AntSize;
  children?: ReactNode;
}) {
  return (
    <ul
      className={cn(
        bordered && "overflow-hidden rounded-xl border border-border",
        className,
      )}
    >
      {renderItem
        ? dataSource.map((item, index) => renderItem(item, index))
        : children}
    </ul>
  );
}

function ListItemMeta({
  title,
  description,
  avatar,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  avatar?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-1 items-start gap-3", className)}>
      {avatar}
      <div className="min-w-0 flex-1">
        {title ? <div className="font-medium">{title}</div> : null}
        {description ? (
          <div className="text-sm text-muted-foreground">{description}</div>
        ) : null}
      </div>
    </div>
  );
}

export const List = Object.assign(ListBase, {
  Item: Object.assign(ListItem, { Meta: ListItemMeta }),
});

/* -------------------------------------------------------------------------- */
/* Dropdown                                                                    */
/* -------------------------------------------------------------------------- */

export type MenuProps = {
  items?: Array<
    | {
        key: string | number;
        label?: ReactNode;
        icon?: ReactNode;
        danger?: boolean;
        disabled?: boolean;
        type?: "divider";
        onClick?: () => void;
      }
    | null
    | undefined
  >;
  onClick?: (info: { key: string; domEvent: React.SyntheticEvent }) => void;
};

export function Dropdown({
  menu,
  children,
  placement,
  trigger: _trigger,
  disabled,
  onOpenChange,
}: {
  menu?: MenuProps;
  children?: ReactNode;
  placement?: string;
  arrow?: boolean;
  destroyPopupOnHide?: boolean;
  getPopupContainer?: unknown;
  trigger?: string[];
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  overlayStyle?: React.CSSProperties;
  className?: string;
}) {
  const align = placement?.toLowerCase().includes("right") ? "end" : "start";
  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger
        disabled={disabled}
        render={children as React.ReactElement}
      />
      <DropdownMenuContent align={align}>
        {(menu?.items ?? []).filter(Boolean).map((item, index) =>
          item!.type === "divider" ? (
            <DropdownMenuSeparator key={item!.key ?? index} />
          ) : (
            <DropdownMenuItem
              key={item!.key ?? index}
              disabled={item!.disabled}
              variant={item!.danger ? "destructive" : "default"}
              onClick={(event) => {
                item!.onClick?.();
                menu?.onClick?.({ key: String(item!.key), domEvent: event });
              }}
            >
              {item!.icon}
              {item!.label}
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* -------------------------------------------------------------------------- */
/* Collapse / Tabs / Segmented / Drawer / Pagination                           */
/* -------------------------------------------------------------------------- */

export function Collapse({
  items = [],
  defaultActiveKey,
  className,
  size: _size,
  activeKey: _activeKey,
  onChange: _onChange,
  bordered: _bordered,
  ghost: _ghost,
  expandIconPosition: _expandIconPosition,
  ...props
}: {
  items?: Array<{
    key: string | number;
    label: ReactNode;
    children: ReactNode;
    [key: string]: unknown;
  }>;
  defaultActiveKey?: Array<string | number> | string | number;
  activeKey?: Array<string | number> | string | number;
  onChange?: (key: string | string[]) => void;
  bordered?: boolean;
  ghost?: boolean;
  expandIconPosition?: string;
  className?: string;
  size?: AntSize;
  style?: React.CSSProperties;
}) {
  const open = Array.isArray(defaultActiveKey)
    ? defaultActiveKey
    : defaultActiveKey != null
      ? [defaultActiveKey]
      : [];
  return (
    <div className={cn("space-y-2", className)} {...props}>
      {items.map((item) => (
        <Collapsible
          key={item.key}
          defaultOpen={open.includes(item.key)}
          className="rounded-3xl border border-border overflow-hidden cursor-pointer"
        >
          <CollapsibleTrigger
            nativeButton
            className="bg-white flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm font-medium cursor-pointer dark:bg-zinc-900"
          >
            {item.label}
            <IconChevronDown size={16} className="text-muted-foreground" />
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t border-zinc-200 pt-3 dark:border-zinc-800">
            <div className="px-4 pb-3">{item.children}</div>
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  );
}

/**
 * antd's legacy `<Tabs.TabPane tab key>` child form, used by the dynamic
 * workflow-input form.
 */
type TabPaneProps = {
  tab?: ReactNode;
  key?: string;
  tabKey?: string;
  children?: ReactNode;
  forceRender?: boolean;
};

export function Tabs({
  items = [],
  activeKey,
  defaultActiveKey,
  onChange,
  className,
  type: _type,
  children,
}: {
  items?: Array<{
    key: string;
    label: ReactNode;
    children?: ReactNode;
    [key: string]: unknown;
  }>;
  activeKey?: string | number;
  mode?: string;
  size?: AntSize;
  tabBarExtraContent?: ReactNode;
  defaultActiveKey?: string | number;
  /** antd's legacy `<TabPane>` children form. */
  children?: ReactNode;
  onChange?: (key: string) => void;
  className?: string;
  type?: string;
}) {
  // One normalised list from either API, so the trigger row and the panels can
  // never disagree about their keys. The legacy `<TabPane>` form was previously
  // passed straight through as `{children}` while `<TabsList>` was built from
  // `items` — empty for those callers — so a legacy `<Tabs>` rendered no
  // triggers AND no matching panels: a blank area where the form should be.
  //
  // A pane's key comes from `tabKey` if given, else from React's own `key`.
  // `Children.toArray` prefixes those (`key="1"` arrives as `".$1"`), hence the
  // strip; the index is a last resort so a pane is never keyless.
  const panes = Children.toArray(children).filter(isValidElement) as Array<
    ReactElement<TabPaneProps>
  >;
  const resolved = items.length
    ? items.map((item) => ({
        key: item.key,
        label: item.label,
        content: item.children,
      }))
    : panes.map((pane, index) => {
        const explicit = pane.props.tabKey;
        const reactKey =
          pane.key == null ? "" : String(pane.key).replace(/^\.\$/, "");
        return {
          key: explicit ?? (reactKey || String(index)),
          label: pane.props.tab,
          content: pane.props.children,
        };
      });

  return (
    <UITabs
      value={activeKey == null ? undefined : String(activeKey)}
      defaultValue={String(defaultActiveKey ?? resolved[0]?.key ?? "")}
      onValueChange={(next) => next != null && onChange?.(String(next))}
      className={className}
    >
      <TabsList>
        {resolved.map((item) => (
          <TabsTrigger key={item.key} value={item.key}>
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {resolved.map((item) =>
        item.content ? (
          <TabsContent key={item.key} value={item.key}>
            {item.content}
          </TabsContent>
        ) : null,
      )}
    </UITabs>
  );
}

export function Segmented<T extends string>({
  options = [],
  value,
  onChange,
  className,
  size: _size,
  block,
}: {
  options?: Array<T | { label: ReactNode; value: T }>;
  value?: T;
  onChange?: (value: T) => void;
  className?: string;
  size?: AntSize;
  style?: React.CSSProperties;
  block?: boolean;
}) {
  const tabs = options.map((option) =>
    typeof option === "string"
      ? { value: option, label: option }
      : { value: option.value, label: segmentedLabelText(option.label) },
  );
  // Per instance, NOT the shared constant `"antd-segmented"` this used to pass.
  // <PillTabs> documents that its `layoutId` must be unique per mounted
  // instance: framer-motion treats every element sharing a `layoutId` as the
  // same shared-layout element, so two Segmenteds on screen at once had one
  // indicator pill between them — it would fly across the page to whichever was
  // clicked last and leave the other with no highlight at all. A single generic
  // shim guarantees that collision the moment a second caller appears.
  const layoutId = useId();
  return (
    <PillTabs
      layoutId={layoutId}
      tabs={tabs}
      value={value ?? tabs[0]?.value ?? ("" as T)}
      onValueChange={(next) => onChange?.(next)}
      className={className}
      block={block}
    />
  );
}

/** PillTabs is label-only; antd Segmented often passed JSX. Never `String(node)`. */
function segmentedLabelText(label: ReactNode): string {
  if (label == null || typeof label === "boolean") return "";
  if (typeof label === "string" || typeof label === "number") {
    return String(label);
  }
  if (Array.isArray(label)) {
    return label
      .map((child) => segmentedLabelText(child as ReactNode))
      .join("");
  }
  if (isValidElement(label)) {
    return segmentedLabelText(
      (label.props as { children?: ReactNode }).children,
    );
  }
  return "";
}

export function Drawer({
  open,
  onClose,
  title,
  placement = "right",
  width: _width,
  closable: _closable,
  extra,
  children,
  className,
}: {
  open?: boolean;
  onClose?: () => void;
  title?: ReactNode;
  placement?: "left" | "right" | "top" | "bottom";
  width?: number | string;
  closable?: boolean;
  extra?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose?.()}>
      <SheetContent side={placement} className={className}>
        {title || extra ? (
          <SheetHeader className="flex-row items-center justify-between">
            {title ? <SheetTitle>{title}</SheetTitle> : null}
            {extra}
          </SheetHeader>
        ) : null}
        {children}
      </SheetContent>
    </Sheet>
  );
}

export function Pagination({
  current = 1,
  pageSize = 10,
  total = 0,
  onChange,
  onShowSizeChange,
  pageSizeOptions,
  showTotal,
  className,
}: {
  current?: number;
  pageSize?: number;
  total?: number;
  onChange?: (page: number, pageSize: number) => void;
  /** antd only — see the note in the body. */
  onShowSizeChange?: (current: number, size: number) => void;
  showTotal?: (total: number, range: [number, number]) => ReactNode;
  pageSizeOptions?: Array<string | number>;
  size?: AntSize;
  className?: string;
  /** antd only — `PaginationBlock` has no page-size control. */
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  simple?: boolean;
}) {
  // `PaginationBlock` is page-count based and has no page-size control, so the
  // page-size callbacks are inert here. No current caller offers a size picker
  // in the UI — `onShowSizeChange` only ever fired from antd's own selector.
  void onShowSizeChange;
  void pageSizeOptions;

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, total);
  const leading = showTotal?.(total, [from, to]);

  return (
    <PaginationBlock
      page={current}
      totalPages={totalPages}
      onPageChange={(page) => onChange?.(page, pageSize)}
      leading={leading}
      className={className}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Table                                                                       */
/* -------------------------------------------------------------------------- */

/** antd's `ColumnsType<T>`, so callers that annotate with it keep compiling. */
export type ColumnsType<T> = Array<TableColumn<T>>;

export type TableColumn<T> = {
  key?: string;
  dataIndex?: string;
  title?: ReactNode;
  width?: number | string;
  align?: "left" | "center" | "right";
  ellipsis?: boolean;
  fixed?: string | boolean;
  sorter?: unknown;
  render?: (value: any, record: T, index: number) => ReactNode;
};

/** Subset of antd's `rowSelection` that the compat table supports. */
export type RowSelection<T> = {
  selectedRowKeys?: React.Key[];
  onChange?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void;
  getCheckboxProps?: (record: T) => { disabled?: boolean; name?: string };
};

export function Table<T extends Record<string, any>>({
  columns = [],
  dataSource = [],
  rowKey,
  onRow,
  loading,
  className,
  locale,
  rowSelection,
}: {
  columns?: Array<TableColumn<T>>;
  dataSource?: readonly T[];
  rowKey?: string | ((record: T) => string);
  onRow?: (
    record: T,
    index: number,
  ) => React.HTMLAttributes<HTMLTableRowElement>;
  loading?: boolean;
  className?: string;
  /** antd's empty-state slot. Only `emptyText` is honoured. */
  locale?: { emptyText?: ReactNode };
  rowSelection?: RowSelection<T>;
  pagination?: unknown;
  scroll?: unknown;
  size?: AntSize;
}) {
  const keyOf = (record: T, index: number) =>
    typeof rowKey === "function"
      ? rowKey(record)
      : rowKey
        ? String(record[rowKey])
        : String(index);

  const selectedKeys = new Set(
    (rowSelection?.selectedRowKeys ?? []).map(String),
  );

  const selectableKeys = dataSource
    .map((record, index) => ({ record, key: keyOf(record, index) }))
    .filter(({ record }) => !rowSelection?.getCheckboxProps?.(record)?.disabled)
    .map(({ key }) => key);

  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selectedKeys.has(key));
  const someSelected = selectableKeys.some((key) => selectedKeys.has(key));

  const emit = (nextKeys: React.Key[]) => {
    const nextSet = new Set(nextKeys.map(String));
    const nextRows = dataSource.filter((record, index) =>
      nextSet.has(keyOf(record, index)),
    );
    rowSelection?.onChange?.(nextKeys, nextRows);
  };

  const toggleAll = () => {
    if (allSelected) {
      emit(
        (rowSelection?.selectedRowKeys ?? []).filter(
          (key) => !selectableKeys.includes(String(key)),
        ),
      );
    } else {
      const merged = new Set<React.Key>([
        ...(rowSelection?.selectedRowKeys ?? []),
        ...selectableKeys,
      ]);
      emit([...merged]);
    }
  };

  const toggleRow = (key: string) => {
    const current = rowSelection?.selectedRowKeys ?? [];
    emit(
      selectedKeys.has(key)
        ? current.filter((k) => String(k) !== key)
        : [...current, key],
    );
  };

  if (loading) return <Spinner className="mx-auto my-8" />;

  return (
    <UITable className={className}>
      <TableHeader>
        <TableRow>
          {rowSelection ? (
            <TableHead className="w-10" onClick={(e) => e.stopPropagation()}>
              <UICheckbox
                aria-label="Select all rows"
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                disabled={selectableKeys.length === 0}
                onCheckedChange={toggleAll}
              />
            </TableHead>
          ) : null}
          {columns.map((column, index) => (
            <TableHead
              key={column.key ?? column.dataIndex ?? index}
              style={column.width ? { width: column.width } : undefined}
              className={cn(
                column.align === "right" && "text-right",
                column.align === "center" && "text-center",
              )}
            >
              {column.title}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {dataSource.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={columns.length + (rowSelection ? 1 : 0)}
              className="h-24 text-center"
            >
              {locale?.emptyText ?? "No data"}
            </TableCell>
          </TableRow>
        ) : (
          dataSource.map((record, index) => {
            const key = keyOf(record, index);
            const checkboxProps = rowSelection?.getCheckboxProps?.(record);
            return (
              <TableRow key={key} {...(onRow?.(record, index) ?? {})}>
                {rowSelection ? (
                  <TableCell
                    className="w-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <UICheckbox
                      aria-label={checkboxProps?.name ?? `Select row ${key}`}
                      checked={selectedKeys.has(key)}
                      disabled={checkboxProps?.disabled}
                      onCheckedChange={() => toggleRow(key)}
                    />
                  </TableCell>
                ) : null}
                {columns.map((column, columnIndex) => (
                  <TableCell
                    key={column.key ?? column.dataIndex ?? columnIndex}
                    className={cn(
                      column.align === "right" && "text-right",
                      column.align === "center" && "text-center",
                    )}
                  >
                    {column.render
                      ? column.render(
                          column.dataIndex
                            ? record[column.dataIndex]
                            : undefined,
                          record,
                          index,
                        )
                      : column.dataIndex
                        ? String(record[column.dataIndex] ?? "")
                        : null}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </UITable>
  );
}

/* -------------------------------------------------------------------------- */
/* Upload                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * antd's `<Upload.Dragger>`. `beforeUpload={() => false}` (every caller) means
 * "do not auto-upload, just hand me the file", which is what `FileDropzone`
 * does — so only `onChange` is wired, in antd's `{ fileList }` shape.
 *
 * `fileList` may contain local `File`s (fresh picks) or remote `{ url, name }`
 * entries (prefilled from a previous run / schedule / input default).
 */
type UploadFileItem = {
  uid?: string;
  name?: string;
  url?: string;
  status?: string;
  originFileObj?: File;
};

function normalizeUploadFiles(
  fileList: unknown[] | undefined,
): UploadFileItem[] {
  if (!Array.isArray(fileList)) return [];
  return fileList.filter(Boolean).map((item, index) => {
    if (item instanceof File) {
      return {
        uid: `${item.name}-${item.size}-${index}`,
        name: item.name,
        originFileObj: item,
      };
    }
    if (typeof item === "string") {
      return {
        uid: `url-${index}`,
        name: item.split("/").pop() || "attachment",
        url: item,
        status: "done",
      };
    }
    const entry = item as UploadFileItem;
    return {
      ...entry,
      uid: entry.uid ?? `${entry.name ?? "file"}-${index}`,
    };
  });
}

function UploadFilePreview({
  file,
  name,
  url,
  listType,
  onRemove,
  disabled,
}: {
  file?: File;
  name: string;
  url?: string;
  listType?: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (listType !== "picture") {
      setPreviewUrl(url ?? null);
      return;
    }
    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setPreviewUrl(url ?? null);
  }, [file, listType, url]);

  if (listType === "picture" && previewUrl) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-2",
          disabled && "pointer-events-none opacity-50",
        )}
      >
        <img
          src={previewUrl}
          alt={name}
          className="size-12 shrink-0 rounded-lg object-cover"
        />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {name}
        </span>
        {!disabled ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="rounded-full"
            aria-label={`Remove ${name}`}
            onClick={onRemove}
          >
            <IconX size={14} />
          </Button>
        ) : null}
      </div>
    );
  }

  return <FileChip name={name} onRemove={onRemove} disabled={disabled} />;
}

function UploadDragger({
  accept,
  multiple,
  disabled,
  onChange,
  children,
  className,
  fileList,
  showUploadList,
  maxCount,
  listType,
}: {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  beforeUpload?: unknown;
  maxCount?: number;
  showUploadList?: boolean;
  listType?: string;
  name?: string;
  onChange?: (info: { fileList: UploadFileItem[] }) => void;
  children?: ReactNode;
  className?: string;
  fileList?: unknown[];
}) {
  const items = normalizeUploadFiles(fileList);

  const emitFileList = (next: UploadFileItem[]) => {
    onChange?.({ fileList: next });
  };

  return (
    <div className="space-y-2">
      <FileDropzoneImpl
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className={className}
        hint={children}
        onFiles={(picked) => {
          const pickedItems: UploadFileItem[] = picked.map((file, index) => ({
            uid: `${file.name}-${file.size}-${index}`,
            originFileObj: file,
            name: file.name,
          }));
          let next = multiple ? [...items, ...pickedItems] : pickedItems;
          if (maxCount === 1) next = pickedItems.slice(0, 1);
          else if (maxCount != null) next = next.slice(0, maxCount);
          emitFileList(next);
        }}
      />
      {showUploadList !== false && items.length > 0 ? (
        <div className="flex flex-col gap-2">
          {items.map((item, index) => (
            <UploadFilePreview
              key={item.uid ?? `${item.name}-${index}`}
              file={item.originFileObj}
              url={item.url}
              name={item.name ?? item.originFileObj?.name ?? "Uploaded file"}
              listType={listType}
              disabled={disabled}
              onRemove={() => {
                emitFileList(items.filter((_, i) => i !== index));
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export const Upload = Object.assign(UploadDragger, { Dragger: UploadDragger });

/* -------------------------------------------------------------------------- */
/* Form                                                                        */
/* -------------------------------------------------------------------------- */

type Rule = { required?: boolean; message?: string };
type Values = Record<string, any>;
/** antd accepts a dotted path as an array: `name={["envs", "API_KEY"]}`. */
type FieldName = string | Array<string | number>;

const pathKey = (name: FieldName) =>
  Array.isArray(name) ? name.join(".") : name;

function readPath(source: Values, name: FieldName) {
  const path = Array.isArray(name) ? name : [name];
  return path.reduce<any>((acc, key) => (acc == null ? acc : acc[key]), source);
}

function writePath(source: Values, name: FieldName, value: unknown): Values {
  const path = Array.isArray(name) ? name : [name];
  const [head, ...rest] = path;
  if (head === undefined) return source;
  if (rest.length === 0) return { ...source, [head]: value };
  return {
    ...source,
    [head]: writePath(
      (source[head] as Values) ?? {},
      rest as Array<string | number>,
      value,
    ),
  };
}

/** A `{}`-literal object, i.e. one worth recursing into. Not arrays or Dates. */
const isPlainObject = (input: unknown): input is Values =>
  typeof input === "object" &&
  input !== null &&
  !Array.isArray(input) &&
  Object.getPrototypeOf(input) === Object.prototype;

/**
 * Recursive merge for `setFieldsValue`, so a partial nested tree updates the
 * paths it names instead of replacing the branch above them.
 */
function mergeValues(base: Values, next: Values): Values {
  const merged: Values = { ...base };
  for (const [key, value] of Object.entries(next)) {
    const existing = merged[key];
    merged[key] =
      isPlainObject(existing) && isPlainObject(value)
        ? mergeValues(existing, value)
        : value;
  }
  return merged;
}

export type FormInstance = {
  getFieldsValue: () => Values;
  setFieldsValue: (values: Values) => void;
  resetFields: () => void;
  validateFields: () => Promise<Values>;
  submit: () => void;
};

type FormStore = FormInstance & {
  subscribe: (listener: () => void) => () => void;
  get: (name: FieldName) => unknown;
  set: (name: FieldName, value: unknown) => void;
  register: (name: FieldName, rules?: Rule[]) => void;
  errorFor: (name: FieldName) => string | undefined;
  bindSubmit: (handler: () => void) => void;
  setInitial: (values: Values) => void;
};

/**
 * A minimal stand-in for antd's `Form.useForm()`.
 *
 * Covers exactly the surface the two dynamic workflow-input forms use:
 * `getFieldsValue` / `setFieldsValue` / `resetFields` / `validateFields` /
 * `submit`, plus `required` rules. It is NOT a general form library — for
 * anything new, use Conform, which the rest of the app already uses.
 */
export function useForm<T = Values>(): [FormStore & { __values?: T }] {
  const store = useRef<FormStore | null>(null);
  if (!store.current) {
    let values: Values = {};
    let initial: Values = {};
    let errors: Record<string, string> = {};
    const rules: Record<string, { name: FieldName; rules: Rule[] }> = {};
    const listeners = new Set<() => void>();
    let submitHandler: (() => void) | null = null;
    const emit = () => listeners.forEach((l) => l());

    store.current = {
      subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      get: (name) => readPath(values, name),
      set(name, value) {
        values = writePath(values, name, value);
        const key = pathKey(name);
        if (errors[key]) {
          const { [key]: _drop, ...rest } = errors;
          errors = rest;
        }
        emit();
      },
      register(name, fieldRules) {
        if (fieldRules) rules[pathKey(name)] = { name, rules: fieldRules };
      },
      errorFor: (name) => errors[pathKey(name)],
      bindSubmit(handler) {
        submitHandler = handler;
      },
      setInitial(next) {
        initial = next;
        // Same per-path merge as setFieldsValue, with the arguments the other way
        // round: anything already entered wins over an initial value.
        values = mergeValues(next, values);
        emit();
      },
      getFieldsValue: () => ({ ...values }),
      setFieldsValue(next) {
        // Merged per path, as antd does — NOT a one-level spread. Both dynamic
        // workflow forms name their fields `["inputs", x]` / `["envs", y]` and
        // call this with a whole `{ inputs, envs }` tree whenever the saved-envs
        // fetch resolves. A shallow merge replaced the entire `inputs` object,
        // so every value the user had typed was dropped on the floor if that
        // request landed mid-edit. Only plain objects recurse; arrays and
        // scalars still replace wholesale.
        values = mergeValues(values, next);
        emit();
      },
      resetFields() {
        values = { ...initial };
        errors = {};
        emit();
      },
      async validateFields() {
        const found: Record<string, string> = {};
        for (const [key, entry] of Object.entries(rules)) {
          for (const rule of entry.rules) {
            const value = readPath(values, entry.name);
            const empty =
              value === undefined ||
              value === null ||
              value === "" ||
              (Array.isArray(value) && value.length === 0);
            if (rule.required && empty) {
              found[key] = rule.message ?? "This field is required";
              break;
            }
          }
        }
        errors = found;
        emit();
        if (Object.keys(found).length) {
          throw {
            errorFields: Object.entries(found).map(([name, message]) => ({
              name: [name],
              errors: [message],
            })),
          };
        }
        return { ...values };
      },
      submit() {
        submitHandler?.();
      },
    };
  }
  return [store.current];
}

const FormContext = createContext<FormStore | null>(null);

function FormBase({
  form,
  initialValues,
  onFinish,
  onFinishFailed,
  layout: _layout,
  className,
  children,
  ref,
}: {
  form?: FormStore;
  initialValues?: Values;
  onFinish?: (values: Values) => void;
  onFinishFailed?: (info: unknown) => void;
  layout?: "horizontal" | "vertical" | "inline";
  className?: string;
  children?: ReactNode;
  ref?: React.Ref<FormInstance>;
}) {
  const [fallback] = useForm();
  const store = form ?? fallback;

  useEffect(() => {
    if (initialValues) store.setInitial(initialValues);
    // Initial values are read once, as in antd.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = useCallback(() => {
    store
      .validateFields()
      .then((values) => onFinish?.(values))
      .catch((info) => onFinishFailed?.(info));
  }, [store, onFinish, onFinishFailed]);

  useEffect(() => store.bindSubmit(submit), [store, submit]);
  useImperativeHandle(ref, () => store, [store]);

  return (
    <FormContext.Provider value={store}>
      <form
        className={cn("space-y-4", className)}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}

function FormItem({
  name,
  label,
  rules,
  tooltip,
  valuePropName,
  getValueFromEvent,
  className,
  children,
}: {
  name?: FieldName;
  label?: ReactNode;
  rules?: Rule[];
  tooltip?: ReactNode;
  /** antd's switch for boolean controls: `valuePropName="checked"`. */
  valuePropName?: string;
  getValueFromEvent?: (...args: any[]) => unknown;
  className?: string;
  children?: ReactNode;
}) {
  const store = useContext(FormContext);
  const [, force] = useState(0);

  const nameKey = name ? pathKey(name) : undefined;
  useEffect(() => {
    if (!store) return;
    if (name) store.register(name, rules);
    return store.subscribe(() => force((n) => n + 1));
    // Keyed on the joined path, since an array `name` is a new identity each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, nameKey]);

  const required = rules?.some((rule) => rule.required);
  const error = store && name ? store.errorFor(name) : undefined;

  let control = children;
  if (store && name && isValidElement(children)) {
    const prop = valuePropName ?? "value";
    const current = store.get(name);
    const child = children as ReactElement<any>;
    control = cloneElement(child, {
      [prop]: current,
      onChange: (...args: any[]) => {
        const next = getValueFromEvent
          ? getValueFromEvent(...args)
          : extractValue(args[0], prop);
        store.set(name, next);
        child.props?.onChange?.(...args);
      },
    });
  }

  return (
    // `group/form-item` exists only so the label can react to its own control
    // being disabled — CSS cannot look forward from a label to a later sibling,
    // but the wrapper can look down with `:has(:disabled)`. antd dimmed the
    // label for free; without this the control faded to 50% while its label
    // stayed at full strength, which read as "greyed out by accident" rather
    // than "disabled".
    <div className={cn("group/form-item space-y-1.5", className)}>
      {label ? (
        <Label className="flex items-center gap-1 group-has-[:disabled]/form-item:opacity-50">
          {label}
          {required ? <span className="text-destructive">*</span> : null}
          {tooltip ? (
            <SimpleTooltip title={tooltip}>
              <span className="cursor-help text-muted-foreground">?</span>
            </SimpleTooltip>
          ) : null}
        </Label>
      ) : null}
      {control}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function extractValue(event: unknown, prop: string) {
  if (event && typeof event === "object" && "target" in event) {
    const target = (event as { target: Record<string, unknown> }).target;
    return prop === "checked" ? target.checked : target.value;
  }
  return event;
}

export const Form = Object.assign(FormBase, { Item: FormItem, useForm });
